/**
 * Administration Validation Utilities
 * 
 * Validates administration timing windows and other administration-related constraints
 * for the patient context medpass workflow.
 */

import dayjs from 'dayjs';
import duration from 'dayjs/plugin/duration';
import relativeTime from 'dayjs/plugin/relativeTime';

dayjs.extend(duration);
dayjs.extend(relativeTime);

/**
 * Validates if administration time is within 24-hour window of scheduled time
 * @param {string|Date|dayjs} scheduledTime - The scheduled administration time
 * @param {string|Date|dayjs} actualTime - The actual administration time  
 * @returns {Object} Validation result with isValid, timeDifference, and message
 */
export const validateAdministrationWindow = (scheduledTime, actualTime = dayjs()) => {
    if (!scheduledTime) {
        return {
            isValid: false,
            error: 'MISSING_SCHEDULED_TIME',
            message: 'Scheduled time is required',
            timeDifference: null
        };
    }

    const scheduled = dayjs(scheduledTime);
    const actual = dayjs(actualTime);

    if (!scheduled.isValid() || !actual.isValid()) {
        return {
            isValid: false,
            error: 'INVALID_TIME_FORMAT',
            message: 'Invalid time format provided',
            timeDifference: null
        };
    }

    const timeDifference = actual.diff(scheduled, 'hours', true);
    const absoluteDifference = Math.abs(timeDifference);

    const isValid = absoluteDifference <= 24;

    let message;
    let severity;

    if (isValid) {
        if (absoluteDifference <= 1) {
            message = `On time (${Math.abs(timeDifference * 60).toFixed(0)} minutes ${timeDifference >= 0 ? 'late' : 'early'})`;
            severity = 'success';
        } else if (absoluteDifference <= 6) {
            message = `Within acceptable range (${absoluteDifference.toFixed(1)} hours ${timeDifference >= 0 ? 'late' : 'early'})`;
            severity = 'info';
        } else {
            message = `Approaching window limit (${absoluteDifference.toFixed(1)} hours ${timeDifference >= 0 ? 'late' : 'early'})`;
            severity = 'warning';
        }
    } else {
        message = `Outside 24-hour window (${absoluteDifference.toFixed(1)} hours ${timeDifference >= 0 ? 'late' : 'early'})`;
        severity = 'error';
    }

    return {
        isValid,
        timeDifference,
        absoluteDifference,
        severity,
        message,
        scheduledTime: scheduled.format(),
        actualTime: actual.format(),
        isEarly: timeDifference < 0,
        isLate: timeDifference > 0
    };
};

/**
 * Validates multiple administrations for window compliance
 * @param {Array} administrations - Array of {scheduledTime, actualTime, medicationName} objects
 * @returns {Object} Summary of validation results
 */
export const validateMultipleAdministrations = (administrations) => {
    if (!Array.isArray(administrations) || administrations.length === 0) {
        return {
            isAllValid: true,
            validCount: 0,
            invalidCount: 0,
            results: [],
            summary: 'No administrations to validate'
        };
    }

    const results = administrations.map((admin, index) => ({
        index,
        medicationName: admin.medicationName || `Medication ${index + 1}`,
        validation: validateAdministrationWindow(admin.scheduledTime, admin.actualTime),
        ...admin
    }));

    const validCount = results.filter(r => r.validation.isValid).length;
    const invalidCount = results.length - validCount;
    const isAllValid = invalidCount === 0;

    return {
        isAllValid,
        validCount,
        invalidCount,
        results,
        summary: `${validCount}/${results.length} administrations within 24-hour window`
    };
};

/**
 * Gets the recommended administration window for a scheduled time
 * @param {string|Date|dayjs} scheduledTime - The scheduled administration time
 * @returns {Object} Window boundaries and recommendations
 */
export const getAdministrationWindow = (scheduledTime) => {
    if (!scheduledTime) {
        return null;
    }

    const scheduled = dayjs(scheduledTime);
    
    if (!scheduled.isValid()) {
        return null;
    }

    const windowStart = scheduled.subtract(24, 'hours');
    const windowEnd = scheduled.add(24, 'hours');
    const optimalStart = scheduled.subtract(1, 'hour');
    const optimalEnd = scheduled.add(1, 'hour');

    return {
        scheduledTime: scheduled,
        windowStart,
        windowEnd,
        optimalStart,
        optimalEnd,
        windowDuration: '48 hours total',
        optimalDuration: '2 hours total',
        recommendation: 'Administer within 1 hour of scheduled time for optimal results'
    };
};

/**
 * Checks if current time is within administration window
 * @param {string|Date|dayjs} scheduledTime - The scheduled administration time
 * @returns {Object} Current window status
 */
export const getCurrentWindowStatus = (scheduledTime) => {
    const window = getAdministrationWindow(scheduledTime);
    if (!window) {
        return {
            canAdminister: false,
            message: 'Invalid scheduled time'
        };
    }

    const now = dayjs();
    const isWithinWindow = now.isAfter(window.windowStart) && now.isBefore(window.windowEnd);
    const isWithinOptimal = now.isAfter(window.optimalStart) && now.isBefore(window.optimalEnd);

    let status, message, severity;

    if (!isWithinWindow) {
        if (now.isBefore(window.windowStart)) {
            status = 'too_early';
            message = `Too early to administer. Window opens ${window.windowStart.fromNow()}`;
            severity = 'error';
        } else {
            status = 'too_late';
            message = `Window closed. Was available until ${window.windowEnd.fromNow()}`;
            severity = 'error';
        }
    } else if (isWithinOptimal) {
        status = 'optimal';
        message = 'Within optimal administration window';
        severity = 'success';
    } else {
        status = 'acceptable';
        message = 'Within acceptable administration window';
        severity = 'warning';
    }

    return {
        canAdminister: isWithinWindow,
        status,
        message,
        severity,
        isWithinOptimal,
        timeUntilWindow: !isWithinWindow && now.isBefore(window.windowStart) 
            ? window.windowStart.diff(now, 'hours', true) 
            : null,
        timeAfterWindow: !isWithinWindow && now.isAfter(window.windowEnd) 
            ? now.diff(window.windowEnd, 'hours', true) 
            : null
    };
};

/**
 * Formats time difference for display
 * @param {number} hours - Time difference in hours
 * @returns {string} Human-readable time difference
 */
export const formatTimeDifference = (hours) => {
    if (hours === null || hours === undefined) {
        return '';
    }

    const absHours = Math.abs(hours);
    
    if (absHours < 1) {
        const minutes = Math.round(absHours * 60);
        return `${minutes} minute${minutes !== 1 ? 's' : ''}`;
    } else if (absHours < 24) {
        return `${absHours.toFixed(1)} hour${absHours !== 1 ? 's' : ''}`;
    } else {
        const days = Math.floor(absHours / 24);
        const remainingHours = Math.round(absHours % 24);
        return `${days} day${days !== 1 ? 's' : ''} ${remainingHours} hour${remainingHours !== 1 ? 's' : ''}`;
    }
};

/**
 * Validates administration data before recording
 * @param {Object} administrationData - Administration data to validate
 * @returns {Object} Validation result
 */
export const validateAdministrationData = (administrationData) => {
    const errors = [];
    const warnings = [];

    // Required fields validation
    const requiredFields = [
        'workflow_id',
        'medication_id', 
        'patient_id',
        'administered_at'
    ];

    requiredFields.forEach(field => {
        if (!administrationData[field]) {
            errors.push(`${field} is required`);
        }
    });

    // Time validation
    if (administrationData.administered_at) {
        const adminTime = dayjs(administrationData.administered_at);
        if (!adminTime.isValid()) {
            errors.push('Invalid administration time format');
        } else if (adminTime.isAfter(dayjs())) {
            errors.push('Administration time cannot be in the future');
        } else if (adminTime.isBefore(dayjs().subtract(7, 'days'))) {
            warnings.push('Administration time is more than 7 days old');
        }
    }

    // Scheduled time window validation
    if (administrationData.scheduled_time && administrationData.administered_at) {
        const windowValidation = validateAdministrationWindow(
            administrationData.scheduled_time,
            administrationData.administered_at
        );
        
        if (!windowValidation.isValid) {
            errors.push(windowValidation.message);
        } else if (windowValidation.severity === 'warning') {
            warnings.push(windowValidation.message);
        }
    }

    return {
        isValid: errors.length === 0,
        errors,
        warnings,
        canProceed: errors.length === 0
    };
};

/**
 * Administration validation constants
 */
export const ADMINISTRATION_CONSTANTS = {
    MAX_WINDOW_HOURS: 24,
    OPTIMAL_WINDOW_HOURS: 1,
    MAX_HISTORICAL_DAYS: 7,
    SEVERITY_LEVELS: {
        SUCCESS: 'success',
        INFO: 'info', 
        WARNING: 'warning',
        ERROR: 'error'
    },
    WINDOW_STATUS: {
        TOO_EARLY: 'too_early',
        OPTIMAL: 'optimal', 
        ACCEPTABLE: 'acceptable',
        TOO_LATE: 'too_late'
    }
};